import { z } from 'zod';

import type { UserNode } from '../../gql/graphql';

// Basic filter schemas
export const talentDiscoveryAiSearchFilter = z.object({
  name: z.string(),
  relevantPromptText: z.string(),
});

// Base schema without optional fields (for OpenAI)
const talentDiscoveryAiSearchPreviousCurrentFilterBase =
  talentDiscoveryAiSearchFilter.extend({
    isCurrent: z.boolean(),
  });

// Full schema with optional backend-added fields (for response type)
export const talentDiscoveryAiSearchPreviousCurrentFilter =
  talentDiscoveryAiSearchPreviousCurrentFilterBase.extend({
    // Optional: frontend-ready filter IDs (added by backend after OpenAI response)
    id: z.string().optional(),
    levelId: z.string().optional(),
  });

// Skill filter schema with optional backend-added fields (for response type)
export const talentDiscoveryAiSearchSkillFilter =
  talentDiscoveryAiSearchFilter.extend({
    // Optional: frontend-ready filter IDs (added by backend after OpenAI response)
    id: z.string().optional(),
    levelId: z.string().optional(),
  });

// School filter schema with optional backend-added fields (for response type)
export const talentDiscoveryAiSearchSchoolFilter =
  talentDiscoveryAiSearchFilter.extend({
    // Optional: frontend-ready filter IDs (added by backend after OpenAI response)
    id: z.string().optional(),
    levelId: z.string().optional(),
  });

export const talentDiscoveryAiSearchRangeFilter = z.object({
  start_value: z.number().nullable(),
  end_value: z.number().nullable(),
  relevantPromptText: z.string(),
  isCurrent: z.boolean(),
});

// NEW: date range filter (ISO date strings, e.g. "2025-01-31")
export const talentDiscoveryAiSearchDateRangeFilter = z.object({
  start_value: z.string().nullable(),
  end_value: z.string().nullable(),
  relevantPromptText: z.string(),
  isCurrent: z.boolean(),
});

// NEW: overall rating filter (1-5)
export const talentDiscoveryAiSearchOverallRatingFilter = z.object({
  name: z.string(),
  relevantPromptText: z.string(),
});

export const talentDiscoveryAiSearchUnknownFilter =
  talentDiscoveryAiSearchFilter.extend({
    explanation: z.string(),
  });

// Predefined values
export const talentDiscoveryAiSearchEducationValues = z.enum([
  'High School',
  'Associate',
  'Bachelor',
  'MBA',
  'Master',
  'Doctor',
]);

export const talentDiscoveryAiSearchEthnicityValues = z.enum([
  'White',
  'Asian',
  'Hispanic',
  'Black',
  'Native',
  'Multiple',
]);

export const talentDiscoveryAiSearchRiskPrestigeSuitabilityValues = z.enum([
  'Very Low',
  'Low',
  'Medium',
  'High',
  'Very High',
]);

export const talentDiscoveryAiSearchEmploymentRelationshipValues = z.enum([
  'Permanent',
  'Contract',
  'Internship',
  'Self-employed',
  'Seasonal',
]);

export const talentDiscoveryAiSearchWorkScheduleValues = z.enum([
  'Full-time',
  'Part-time',
]);

export const talentDiscoveryAiSearchGenderValues = z.enum(['Male', 'Female']);

export const talentDiscoveryAiSearchSeniorityValues = z.enum([
  'Entry Level',
  'Junior',
  'Associate',
  'Manager',
  'Director',
  'Executive',
  'Senior Executive',
]);

// Filter schemas with predefined values
export const talentDiscoveryAiSearchEducationFilter =
  talentDiscoveryAiSearchFilter.extend({
    name: talentDiscoveryAiSearchEducationValues,
  });

export const talentDiscoveryAiSearchEthnicityFilter =
  talentDiscoveryAiSearchFilter.extend({
    name: talentDiscoveryAiSearchEthnicityValues,
  });

export const talentDiscoveryAiSearchRiskPrestigeSuitabilityFilter =
  talentDiscoveryAiSearchPreviousCurrentFilterBase.extend({
    name: talentDiscoveryAiSearchRiskPrestigeSuitabilityValues,
  });

export const talentDiscoveryAiSearchEmploymentRelationshipFilter =
  talentDiscoveryAiSearchPreviousCurrentFilterBase.extend({
    name: talentDiscoveryAiSearchEmploymentRelationshipValues,
  });

export const talentDiscoveryAiSearchWorkScheduleFilter =
  talentDiscoveryAiSearchPreviousCurrentFilterBase.extend({
    name: talentDiscoveryAiSearchWorkScheduleValues,
  });

export const talentDiscoveryAiSearchGenderFilter =
  talentDiscoveryAiSearchFilter.extend({
    name: talentDiscoveryAiSearchGenderValues,
  });

export const talentDiscoveryAiSearchSeniorityFilter =
  talentDiscoveryAiSearchPreviousCurrentFilterBase.extend({
    name: talentDiscoveryAiSearchSeniorityValues,
  });

// Main filter schema
export const talentDiscoveryAiSearchFilters = z
  .object({
    company: z.array(talentDiscoveryAiSearchPreviousCurrentFilter).nullable(),
    industry: z.array(talentDiscoveryAiSearchPreviousCurrentFilter).nullable(),
    highest_degree: z.array(talentDiscoveryAiSearchEducationFilter).nullable(),
    ethnicity: z.array(talentDiscoveryAiSearchEthnicityFilter).nullable(),
    flight_risk: z
      .array(talentDiscoveryAiSearchRiskPrestigeSuitabilityFilter)
      .nullable(),
    gender: z.array(talentDiscoveryAiSearchGenderFilter).nullable(),
    geography: z.array(talentDiscoveryAiSearchPreviousCurrentFilter).nullable(),
    name: talentDiscoveryAiSearchFilter.nullable(),
    prestige: z
      .array(talentDiscoveryAiSearchRiskPrestigeSuitabilityFilter)
      .nullable(),
    remote_suitability: z
      .array(talentDiscoveryAiSearchRiskPrestigeSuitabilityFilter)
      .nullable(),
    employment_relationship: z
      .array(talentDiscoveryAiSearchEmploymentRelationshipFilter)
      .nullable(),
    work_schedule: z
      .array(talentDiscoveryAiSearchWorkScheduleFilter)
      .nullable(),
    role: z.array(talentDiscoveryAiSearchPreviousCurrentFilter).nullable(),
    school: z.array(talentDiscoveryAiSearchSchoolFilter).nullable(),
    seniority: z.array(talentDiscoveryAiSearchSeniorityFilter).nullable(),
    skill: z.array(z.array(talentDiscoveryAiSearchSkillFilter)).nullable(),
    keywords: z.array(z.array(talentDiscoveryAiSearchFilter)).nullable(),
    salary_total: z.array(talentDiscoveryAiSearchRangeFilter).nullable(),
    titles: z
      .array(z.array(talentDiscoveryAiSearchPreviousCurrentFilter))
      .nullable(),
    graduation_year: talentDiscoveryAiSearchRangeFilter.nullable(),
    years_of_experience: talentDiscoveryAiSearchRangeFilter.nullable(),

    // NEW FILTERS
    date_range: talentDiscoveryAiSearchDateRangeFilter.nullable(),
    overall_rating: z
      .array(talentDiscoveryAiSearchOverallRatingFilter)
      .nullable(),
  })
  .required();

// Main response schema
// Schema for OpenAI (without optional backend-added fields)
export const talentDiscoveryAiSearchFiltersForOpenAI = z
  .object({
    company: z
      .array(talentDiscoveryAiSearchPreviousCurrentFilterBase)
      .nullable(),
    industry: z
      .array(talentDiscoveryAiSearchPreviousCurrentFilterBase)
      .nullable(),
    highest_degree: z.array(talentDiscoveryAiSearchEducationFilter).nullable(),
    ethnicity: z.array(talentDiscoveryAiSearchEthnicityFilter).nullable(),
    flight_risk: z
      .array(talentDiscoveryAiSearchRiskPrestigeSuitabilityFilter)
      .nullable(),
    gender: z.array(talentDiscoveryAiSearchGenderFilter).nullable(),
    geography: z
      .array(talentDiscoveryAiSearchPreviousCurrentFilterBase)
      .nullable(),
    name: talentDiscoveryAiSearchFilter.nullable(),
    prestige: z
      .array(talentDiscoveryAiSearchRiskPrestigeSuitabilityFilter)
      .nullable(),
    remote_suitability: z
      .array(talentDiscoveryAiSearchRiskPrestigeSuitabilityFilter)
      .nullable(),
    employment_relationship: z
      .array(talentDiscoveryAiSearchEmploymentRelationshipFilter)
      .nullable(),
    work_schedule: z
      .array(talentDiscoveryAiSearchWorkScheduleFilter)
      .nullable(),
    role: z.array(talentDiscoveryAiSearchPreviousCurrentFilterBase).nullable(),
    school: z.array(talentDiscoveryAiSearchSchoolFilter).nullable(),
    seniority: z.array(talentDiscoveryAiSearchSeniorityFilter).nullable(),
    skill: z.array(z.array(talentDiscoveryAiSearchSkillFilter)).nullable(),
    keywords: z.array(z.array(talentDiscoveryAiSearchFilter)).nullable(),
    salary_total: z.array(talentDiscoveryAiSearchRangeFilter).nullable(),
    titles: z
      .array(z.array(talentDiscoveryAiSearchPreviousCurrentFilterBase))
      .nullable(),
    graduation_year: talentDiscoveryAiSearchRangeFilter.nullable(),
    years_of_experience: talentDiscoveryAiSearchRangeFilter.nullable(),

    // NEW FILTERS
    date_range: talentDiscoveryAiSearchDateRangeFilter.nullable(),
    overall_rating: z
      .array(talentDiscoveryAiSearchOverallRatingFilter)
      .nullable(),
  })
  .required();

export const talentDiscoveryAiSearchResponseSchemaV2ForOpenAI = z.object({
  filters: talentDiscoveryAiSearchFiltersForOpenAI,
  unknownFilters: z.array(talentDiscoveryAiSearchUnknownFilter),
});

export const talentDiscoveryAiSearchResponseSchemaV2 = z.object({
  filters: talentDiscoveryAiSearchFilters,
  unknownFilters: z.array(talentDiscoveryAiSearchUnknownFilter),
});

// Type inference
export type TalentDiscoveryAiSearchFilterType = z.infer<
  typeof talentDiscoveryAiSearchFilter
>;
export type TalentDiscoveryAiSearchPreviousCurrentFilterType = z.infer<
  typeof talentDiscoveryAiSearchPreviousCurrentFilter
>;
export type TalentDiscoveryAiSearchFiltersType = z.infer<
  typeof talentDiscoveryAiSearchFilters
>;

// V2 format response type using UserNode expression tree (final API response format)
export type TalentDiscoveryAiFilterSearchResponseV2 = {
  filters: {
    expr: UserNode;
    roles_tax: string;
    skills_tax: number;
    roles_tax_id?: number;
    skills_tax_id?: number;
    schoolNames?: Array<{
      rsid: string;
      name: string;
    }>;
    companyNames?: Array<{
      rcid: number;
      name: string;
      // RICS ancestry from the company mapper, so the dashboard can build the
      // path-encoded company id (`${rics_k10}_${rics_k400}_${rcid}`) and resolve
      // the adaptive role/skill taxonomy natively — same as a company-search pick.
      rics_k10?: number | null;
      rics_k50?: number | null;
      rics_k400?: number | null;
    }>;
  };
  unknownFilters: z.infer<typeof talentDiscoveryAiSearchUnknownFilter>[];
  relevantPromptText: {
    valid: string[];
    invalid: string[];
  };
  // Non-TD filters: These are not included in the TD expression tree (expr)
  overall_rating?: z.infer<typeof talentDiscoveryAiSearchOverallRatingFilter>[];
  date_range?: z.infer<typeof talentDiscoveryAiSearchDateRangeFilter>;
};
